import type React from 'react'
import type { JSX } from 'react'

import { createContext, useCallback, useState } from 'react'
import { ProgressLine } from 'atoms/ProgressLine'
import { LazyLoadExternal } from 'blocks/Grid/components/LazyLoad/LazyLoad'
import { ChannelProgramListSC } from '../ProgramList.styled'

// Context for throwing props without cloneElement, because this triggers extra renderings on TVCardEPG image
export const ChannelProgramRowContext = createContext<{
  step?: number
  startTimeLine?: number
  stopTimeLine?: number
  tableGap?: number
}>({})

interface IPaginationInfo {
  [key: number]: number
}

export interface IChannelProgramList {
  children: React.ReactNode | React.ReactNode[]
  showProgressLine: boolean
  progressPosition?: number
  step?: number
  startTimeLine: number
  stopTimeLine: number
  selectedCategoryTabId: number | null
  onLoadMore?: () => void
  percentToTrigger?: number
  tableGap?: number
}

export const ChannelProgramList = ({
  tableGap,
  children,
  showProgressLine,
  progressPosition,
  step,
  startTimeLine,
  stopTimeLine,
  selectedCategoryTabId,
  onLoadMore,
  percentToTrigger,
}: IChannelProgramList): JSX.Element => {
  const [paginationInfo, setPaginationInfo] = useState<IPaginationInfo>({})

  const onSaveState = useCallback(
    (state: number): void => {
      if (selectedCategoryTabId != null) {
        setPaginationInfo({ ...paginationInfo, [selectedCategoryTabId]: state })
      }
    },
    [selectedCategoryTabId, paginationInfo]
  )

  const lazyLoadState =
    selectedCategoryTabId != null
      ? paginationInfo[selectedCategoryTabId]
      : undefined

  const exactLazyLoadState = lazyLoadState || 0

  return (
    <LazyLoadExternal
      onLoadMore={onLoadMore}
      percentToTrigger={percentToTrigger}
      state={exactLazyLoadState}
      onSaveState={onSaveState}
    >
      <ChannelProgramListSC>
        <ChannelProgramRowContext
          value={{
            step,
            startTimeLine,
            stopTimeLine,
            tableGap,
          }}
        >
          {children}
        </ChannelProgramRowContext>
        {showProgressLine && <ProgressLine position={progressPosition} />}
      </ChannelProgramListSC>
    </LazyLoadExternal>
  )
}
